import _ from 'lodash';
import moment from 'moment';
import {
  PATH_CREATOR_ABOUT,
  TRACK_BF_DEAL_CLICKED,
  PATH_FAQ_DIGITAL_BOOK,
  PATH_FAQ_PAGE_COUNT
} from 'constants';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouter } from 'next/router';
import { AnalyticsConsumer } from 'contexts/AnalyticsProvider';
import { UserPreferencesConsumer } from 'contexts/UserPreferencesProvider';
import {
  motion, useTransform, useScroll, useSpring
} from 'framer-motion';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Image from 'components/Image';
import Grid from '@mui/material/Grid';
import Link from 'next/link';
import MuiLink from '@mui/material/Link';
import makeClasses from 'hooks/useClasses';
import PropTypes from 'prop-types';
import styles from 'styles/jss/components/bookSizeDialogStyle';
import React, { useRef, useState } from 'react';
import RichTextRenderer from 'components/Text/RichTextRenderer';
import Typography from '@mui/material/Typography';
import useAlbumAdapter from 'adapters/albumAdapter';

// The alpha transparency PNGs don't compress well out of webiny
const PRODUCT_IMAGES = {
  2699: '/img/products/book_85x11.png',
  2185: '/img/products/book_85x85.png'
};

const CurrentDeal = ({ products }) => {
  const router = useRouter();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { createProject } = useAlbumAdapter();
  const { setAppState } = UserPreferencesConsumer();
  const { track } = AnalyticsConsumer();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ref = useRef(null);
  const classes = makeClasses(styles);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end end']
  });

  const handPosition = useTransform(scrollYProgress, [0.2, 0.3], [0, 1]);
  const handPositionSpring = useSpring(handPosition, { stiffness: 52, damping: 10, mass: 1 });

  const features = (
    <Stack
      direction="row"
      spacing={0}
      justifyContent="left"
      sx={{
        my: 3,
        width: '100%',
        flexWrap: 'wrap',
        '.MuiChip-root': {
          mb: 2,
          mr: 2
        }
      }}

    >
      <Chip label="Matte Cover" size="small" />
      <Chip label="#100 Paper" size="small" />
      <Chip
        label="Includes 24 Pages"
        size="small"
        onClick={() => router.push(PATH_FAQ_PAGE_COUNT)}
      />
      <Chip
        label="Free Digital Book"
        size="small"
        onClick={() => router.push(PATH_FAQ_DIGITAL_BOOK)}
      />
    </Stack>
  );

  const createProduct = async (productId, forceCreate = false) => {
    track(TRACK_BF_DEAL_CLICKED);
    if (isAuthenticated) {
      // Immediately toggle the selector
      setIsSubmitting(true);
      const newProject = await createProject(productId, forceCreate);
      router.push({
        pathname: PATH_CREATOR_ABOUT,
        query: { project: newProject.uuid }
      });
    } else {
      // We can't use the loginWithRedirect{ appState })
      // because we're not using the onRedirectCallback (which is utilizing localStorage anyway)
      setAppState('authState', {
        productId,
        returnTo: `${PATH_CREATOR_ABOUT}?productId=${productId}`
      });
      loginWithRedirect();
    }
  };

  const currentPromo = _.get(products, '0.promo');

  return (
    <Grid
      ref={ref}
      container
      item
      className={classes.dealSection}
      spacing={10}
      sx={{ px: 5 }}
    >
      {currentPromo && (
        <Grid item xs={12} textAlign="center">
          <Typography
            component="h2"
            variant="bookPreview"
            color="primary"
            className="dealHeader"
          >
            {currentPromo.title}
            {' - '}
            {currentPromo.discountPercentageLabel}
            {' '}
            Off
          </Typography>
          <Typography
            variant="bookPreview"
            color="notBlack"
            className="dealSubHeader"
          >
            Use Code &quot;
            {currentPromo.promoCode}
            &quot; at Checkout. Offer valid through
            {' '}
            {moment(currentPromo.effectiveEndDate).format('MMMM Do')}
          </Typography>
        </Grid>
      )}
      {products.map((prod) => (
        <Grid
          key={prod.entryId}
          className="productImage"
          item
          xs={12}
          md={6}
        >
          <Card elevation={6} sx={{ borderRadius: 3, pb: 4 }}>
            <Box
              display="flex"
              height={270}
              alignContent="center"
              sx={{
                background: [`
                  linear-gradient(
                    160deg,
                    #f5e3e5 0 20%,
                    #e6cace 60% 100%
                  )
                `]
              }}
            >
              <Box className="imageBox" width="100%" height={252}>
                <Image
                  src={_.get(PRODUCT_IMAGES, [prod.productId])}
                  alt={prod.title}
                  layout="fill"
                  objectFit="contain"
                />
                <motion.div
                  className="discountBadge"
                  style={{
                    scale: handPositionSpring
                  }}
                >
                  <strong>
                    {prod.promo.discountPercentageLabel}
                  </strong>
                  <small>O F F !</small>
                </motion.div>
              </Box>
            </Box>
            <Box
              className="cartActions"
            >
              <Typography
                component="h2"
                variant="h3"
                color="notBlack"
                className="productTitle"
              >
                {prod.title}
              </Typography>
              <Typography
                className="pricing"
              >
                <strong>
                  &#36;
                  {prod.price}
                  *
                </strong>
                {' '}
                <strike>
                  &#36;
                  {prod.originalPrice}
                </strike>
              </Typography>
              {features}
              <Typography
                className="description"
                sx={{
                  minHeight: {
                    xs: 'auto',
                    md: 145,
                    lg: 135
                  }
                }}
              >
                <RichTextRenderer
                  data={prod.description}
                />
              </Typography>
              <LoadingButton
                variant="contained"
                size="large"
                loading={isSubmitting}
                onClick={() => createProduct(prod.productId)}
                sx={{ mt: 3, width: '100%' }}
              >
                Customize Your Book
              </LoadingButton>
              <Typography>
                <Link href={prod.url} passHref>
                  <MuiLink
                    color="primary"
                    underline="hover"
                    component="a"
                    mt={2}
                    display="block"
                  >
                    Learn more
                  </MuiLink>
                </Link>
              </Typography>
            </Box>
          </Card>
        </Grid>
      ))}
      <Grid item xs={12} textAlign="center">
        <small>
          *Price includes 24 pages (1 image/page).
          Add as many pages as you like for $0.30 per additional page.
        </small>
      </Grid>
    </Grid>
  );
};

CurrentDeal.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export default CurrentDeal;
